import { useCallback, useDeferredValue, useEffect, useRef, useState, useMemo } from 'react';
import { IOfficeOption } from './Types';

interface IUseOfficeSearchProps {
  options: IOfficeOption[];
  selectedOffice?: IOfficeOption;
  onOfficeSelect?: (office: IOfficeOption) => void;
  onCloseOptions: () => void;
}

export const useOfficeSearch = ({
  options,
  selectedOffice,
  onOfficeSelect,
  onCloseOptions,
}: IUseOfficeSearchProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [optimisticSelection, setOptimisticSelection] = useState<IOfficeOption | undefined>(
    selectedOffice,
  );
  const inputRef = useRef<HTMLInputElement>(null);

  const deferredSearchQuery = useDeferredValue(searchQuery);

  const filteredOptions = useMemo(() => {
    if (!deferredSearchQuery.trim()) return options;

    const lowerQuery = deferredSearchQuery.toLowerCase();
    return options.filter((option) =>
      option.label.toLowerCase().includes(lowerQuery)
    );
  }, [options, deferredSearchQuery]);

  useEffect(() => {
    setOptimisticSelection(selectedOffice);
  }, [selectedOffice]);

  const handleOfficeSelect = useCallback(
    (office: IOfficeOption) => {
      setOptimisticSelection(office);
      onOfficeSelect?.(office);
      onCloseOptions();
      setSearchQuery('');
    },
    [onOfficeSelect, onCloseOptions],
  );

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  return {
    searchQuery,
    optimisticSelection,
    filteredOptions,
    inputRef,
    handleOfficeSelect,
    handleSearchChange,
  };
};
